// Lib
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { isEqual, partition } from 'lodash';

// Selectors
import { getUserMentionSuggestions } from '../../mentions/mentionsSelector';

// Utils
import { filterAndSortAssignmentSuggestions } from '../../mentions/filterAndSortSuggestions';

/**
 * A user suggestion row from the mention suggestion selectors. Loosely typed
 * as the mention stack is untyped JS; `id`/`name` feed the assignment attrs.
 */
export type AssignmentSuggestion = {
    id: string;
    name: string;
    disabled?: boolean;
    [key: string]: unknown;
};

export type AssignmentSuggestionGroups = {
    enabledSuggestions: AssignmentSuggestion[];
    disabledSuggestions: AssignmentSuggestion[];
};

/**
 * Sources assignee-picker suggestions for the current board, filtered by
 * `searchTerm` with the currently assigned user pinned first, partitioned into
 * enabled (sharing the board) and disabled (not sharing) groups.
 *
 * Board-scoped redux only — unlike the element popup's
 * `selectFilteredSuggestions` the caller supplies `currentlyAssignedUserId`
 * (e.g. from task item node attrs), so this is reusable by any picker surface.
 */
export const useAssignmentSuggestions = ({
    searchTerm,
    currentlyAssignedUserId,
}: {
    searchTerm: string;
    currentlyAssignedUserId: string | null;
}): AssignmentSuggestionGroups => {
    // Deep equality mirrors the legacy `createDeepSelector` popup selector: the
    // mention objects are rebuilt (new refs) whenever an upstream board/user
    // selector changes ref, so without this any such churn while the picker is
    // open would re-render it. `isEqual` short-circuits on identical refs, so
    // the deep compare only runs when the refs actually change.
    const possibleSuggestions = useSelector(getUserMentionSuggestions, isEqual) as AssignmentSuggestion[];

    return useMemo(() => {
        // Copied because `filterAndSortAssignmentSuggestions` sorts in place and
        // the selector output is memoised.
        const suggestions: AssignmentSuggestion[] = filterAndSortAssignmentSuggestions(
            searchTerm,
            currentlyAssignedUserId,
            [...possibleSuggestions],
        );

        const [enabledSuggestions, disabledSuggestions] = partition(suggestions, (suggestion) => !suggestion.disabled);

        return { enabledSuggestions, disabledSuggestions };
    }, [searchTerm, currentlyAssignedUserId, possibleSuggestions]);
};
